import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * ProtectedRoute - Guards routes based on authentication and permissions.
 *
 * Props:
 *   - allowedRoles: ['admin'] | ['employee'] | ['admin', 'employee'] — legacy role-based check
 *   - requiredPermission: string — optional permission key (e.g., 'analytics.view')
 *   - requiredScope: string — optional scope for scoped permissions (e.g., 'team', 'all')
 *
 * Logic:
 *   1. If not authenticated → redirect to /login
 *   2. If preboarding employee accessing /employee → redirect to preboarding dashboard
 *   3. If allowedRoles specified → check role (admin or employee)
 *   4. If requiredPermission specified → check against user's permissions
 *   5. If company creator hasn't completed setup → redirect to /admin/setup
 */
const ProtectedRoute = ({ children, allowedRoles, requiredPermission, requiredScope }) => {
    const { user, loading, checkPermission } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // If employee is in preboarding lifecycle_stage and trying to access /employee, redirect to preboarding
    if (!user.is_admin && user.lifecycle_stage === 'preboarding' && user.preboarding_token) {
        if (!window.location.pathname.includes('/preboard/')) {
            return <Navigate to={`/preboard/${user.preboarding_token}/dashboard`} replace />;
        }
    }

    // Role-based access check (legacy — admin vs employee)
    if (allowedRoles && !allowedRoles.includes(user.is_admin ? 'admin' : 'employee')) {
        return <Navigate to="/" replace />;
    }

    // Permission-based access check (new — checks specific permission key)
    if (requiredPermission && !checkPermission(requiredPermission, requiredScope)) {
        // User doesn't have the required permission — redirect to their home
        const homePath = user.is_admin ? '/admin' : '/employee';
        return <Navigate to={homePath} replace />;
    }

    // Redirect company owner to setup wizard if not completed
    if (user.is_company_creator && !user.setup_completed && !window.location.pathname.includes('/admin/setup')) {
        return <Navigate to="/admin/setup" replace />;
    }

    return children;
};

export default ProtectedRoute;
